import React, { useState } from "react";
import {
  Box,
  Button,
  Center,
  Group,
  Loader,
  Paper,
  Space,
  Text,
  Textarea,
} from "@mantine/core";
import { closeAllModals, openModal } from "@mantine/modals";
import { useToasts } from "react-toast-notifications";
import Moment from "react-moment";
import { Icon } from "../Atoms/Icons";
import { FormSubmissionS } from "../Service/FormSubmissionS";

const getTranslationKey = (documentType, suffix) =>
  `${documentType === "OFFER" ? "offer" : "contract"}${suffix}`;

const CANCELLATION_ACTION_ENABLED = false;

export const CancelledFormSubmissionNotice = ({
  cancelledAt,
  documentType,
  t,
}) => {
  const translate = (suffix) => t(getTranslationKey(documentType, suffix));

  return (
    <Paper
      withBorder
      radius="md"
      p="md"
      mb={30}
      style={{ backgroundColor: "#FFF5F5", borderColor: "#dee2e6" }}
    >
      <Group noWrap align="flex-start" spacing="sm">
        <Box
          style={{
            alignItems: "center",
            backgroundColor: "#ffffff",
            borderRadius: "50%",
            color: "#c92a2a",
            display: "flex",
            flex: "0 0 34px",
            height: 34,
            justifyContent: "center",
          }}
        >
          <Icon value="CANCELED" />
        </Box>
        <Box>
          <Text weight={600} style={{ color: "#212529" }}>
            {translate("CancelledHeadline")}
          </Text>
          <Text size="sm" mt={4} style={{ color: "#6c757d" }}>
            {translate("CancelledTextStart")} {" "}
            <Moment format="L">{cancelledAt}</Moment>
            {translate("CancelledTextTimePrefix")} {" "}
            <Moment format="LT">{cancelledAt}</Moment>
            {translate("CancelledTextEnd")}
          </Text>
        </Box>
      </Group>
    </Paper>
  );
};

export const CancelFormSubmissionAction = ({
  documentType,
  formSubmissionId,
  lastSubmitted,
  reloadFormSubmission,
  t,
}) => {
  const { addToast } = useToasts();
  const translate = (suffix) => t(getTranslationKey(documentType, suffix));

  if (!CANCELLATION_ACTION_ENABLED) {
    return null;
  }

  if (!FormSubmissionS.isCancellationAllowed(lastSubmitted)) {
    return null;
  }

  const onClick = () =>
    openModal({
      title: (
        <Text weight="bolder" size="xl">
          {translate("WithdrawTitle")}
        </Text>
      ),
      children: (
        <CancelFormSubmissionModal
          formSubmissionId={formSubmissionId}
          reloadFormSubmission={reloadFormSubmission}
          addToast={addToast}
          translate={translate}
          t={t}
        />
      ),
    });

  return (
    <>
      <Space h="xl" />
      <hr />
      <Center>
        <Button
          color="red"
          variant="outline"
          style={{ height: 42, marginTop: 12 }}
          onClick={onClick}
        >
          {translate("WithdrawButton")}
        </Button>
      </Center>
    </>
  );
};

const CancelFormSubmissionModal = ({
  formSubmissionId,
  reloadFormSubmission,
  addToast,
  translate,
  t,
}) => {
  const [reasonOfCancellation, setReasonOfCancellation] = useState("");
  const [loading, setLoading] = useState(false);

  const cancelFormSubmission = (event) => {
    event.preventDefault();
    setLoading(true);
    FormSubmissionS.cancelFormSubmission(
      formSubmissionId,
      reasonOfCancellation.trim()
    )
      .then((resp) => {
        if (resp.ok) {
          closeAllModals();
          if (reloadFormSubmission) {
            reloadFormSubmission();
          }
          addToast(translate("WithdrawSuccess"), {
            appearance: "success",
          });
        } else {
          addToast(translate("WithdrawError"), { appearance: "error" });
        }
      })
      .catch(() => {
        addToast(translate("WithdrawConnectionError"), {
          appearance: "error",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <form onSubmit={cancelFormSubmission}>
      <Text mb="md">{translate("WithdrawPrompt")}</Text>
      <Textarea
        minRows={4}
        name="reason_of_cancellation"
        value={reasonOfCancellation}
        onChange={(e) => setReasonOfCancellation(e.target.value)}
        placeholder={translate("WithdrawReasonPlaceholder")}
      />
      <Group position="right" mt={30}>
        <Button
          variant="default"
          disabled={loading}
          onClick={() => closeAllModals()}
        >
          {t("cancelButton")}
        </Button>
        <Button
          color="red"
          type="submit"
          disabled={loading}
          rightIcon={loading && <Loader size="xs" />}
        >
          {translate("WithdrawButton")}
        </Button>
      </Group>
    </form>
  );
};
